import React, { Fragment, useState } from 'react'
import { makeStyles } from '@mui/styles'
import { Theme } from '@mui/material/styles'
import { ErrorMessage, Field, Form, Formik } from 'formik'
import { CheckboxWithLabel, Select, TextField } from 'formik-mui'
import { User, UserCreate } from '../../interfaces/User/UserWMS'
import { useWarehouses, useZones } from '../../hooks'
import {
  Chip,
  Grid,
  Tooltip,
  IconButton,
  InputLabel,
  MenuItem,
  Button
} from '@mui/material'
import { apiCall, ApiConfig } from '../../config/api'
import { SnackbarState } from '../../interfaces/Generic/State'
import { Zone } from '../../interfaces'
import Info from '@mui/icons-material/Info'
import { number, object, string } from 'yup'

/*
Copyright 2022-09-06 por Miguel Balloqui Ecomsur Holding S.A ©
Detalle: Agrega logica para obtener zonas por almacen, y asociarlas al usuario

Copyright 2023-09-11 por Alfonso Moya Ecomsur Holding S.A ©
Detalle: se permite edición de campos email y alias de usuario
*/

interface CreateUserProps {
  user?: User
  handleSnackbar: (state: SnackbarState) => void
  closeModal: () => void
}

const useStyles = makeStyles((theme: Theme) => ({
  chipBoxZones: {
    display: 'flex',
    justifyContent: 'center',
    flexWrap: 'wrap',
    '& > *': {
      margin: theme.spacing(0.5)
    }
  },
  button: {
    animation: `$sonar-effect 1s ease-in-out .1s infinite`
  },
  '@keyframes sonar-effect': {
    '0%': {
      opacity: 1
    },
    '25%': {
      opacity: 1,
      boxShadow: '0 0 0 5px #fff, 0 0 10px 10px #fff, 0 0 0 10px #fff'
    },
    '50%': {
      opacity: 1,
      boxShadow: '0 0 0 5px #fff, 0 0 10px 10px #fff, 0 0 0 10px #fff'
    },
    '75%': {
      opacity: 1,
      boxShadow: '0 0 0 5px #fff, 0 0 10px 10px #fff, 0 0 0 10px #fff'
    },
    '100%': {
      boxShadow: '0 0 0 5px #fff, 0 0 10px 10px #fff, 0 0 0 10px #fff',
      transform: 'scale(1.3)',
      opacity: 1
    }
  }
}))

const initialValues: User = {
  userId: 0,
  userName: '',
  userAlias: '',
  contactNumber: '',
  active: true,
  warehouseId: '' as unknown as number,
  zones: []
}

const userValidationSchema = object({
  userName: string()
    .trim()
    .required('Debe ingresar un email.')
    .max(150, 'Puede componerse por un máximo de 50 caracteres')
    .email('El email no es válido.'),
  userAlias: string()
    .trim()
    .required('Debe ingresar un nombre.')
    .max(80, 'Puede componerse por un máximo de 80 caracteres'),
  warehouseId: number()
    .required('Debe seleccionar un almacén.')
    .min(1, 'Debe seleccionar un almacén.')
})

export const CreateUser: React.FC<CreateUserProps> = ({
  user,
  handleSnackbar,
  closeModal
}) => {
  const { chipBoxZones, button } = useStyles()
  const {
    warehousesState: { warehouses },
    getWarehouses
  } = useWarehouses()

  // Obtener Zonas
  const {
    zonesState: { zones },
    getZones
  } = useZones()

  React.useEffect(() => {
    if (warehouses?.length === 0) {
      getWarehouses()
    }
  }, [])

  React.useEffect(() => {
    if (zones?.length === 0) {
      getZones()
    }
  }, [])

  const [userZones, setUserZones] = React.useState<number[]>([])
  const [warehouseId, setWarehouseId] = React.useState<number>(-1)
  const [zonesByWarehouse, setZonesByWarehouse] = useState<Zone[]>([])
  const [zonesErrorMessage, setZonesErrorMessage] = useState<string>('')

  const handleSelectedWarehouseZones = (
    zones: Zone[],
    warehouseSelected: number = 0
  ) => {
    // Setea las zonas por almacen al inicio
    if (warehouseId > 0 && warehouseSelected === 0) {
      const initialZones = [...zones!]

      const filteredZones = initialZones.filter(
        (x: Zone) => x.warehouseId === warehouseId
      )

      setZonesByWarehouse(filteredZones)
    }

    // Setea las zonas asociadas al usuario
    // cuando se cambia de almacen
    if (warehouseSelected > 0) {
      if (warehouseSelected !== warehouseId) {
        setUserZones([])
        setWarehouseId(warehouseSelected)
      }
    }

    // resetea las zonas por almacen y las asociadas al usuario
    // cuando el almacen id es menor a cero
    if (warehouseSelected < 0) {
      setZonesErrorMessage('')
      setUserZones([])
      setWarehouseId(warehouseSelected)
      setZonesByWarehouse([])
    }
  }

  const handleClickedChip = (zoneId: number) => {
    setZonesErrorMessage('')
    // ver si está seleccionado
    const existOnUserZones = userZones.includes(zoneId)

    if (existOnUserZones) {
      const userZonesWithoutClickedZone = userZones.filter(
        (zone) => zone !== zoneId
      )
      setUserZones(userZonesWithoutClickedZone)
    } else {
      setUserZones([...userZones, zoneId])
    }
  }

  const handleSubmit = async (
    values: User,
    setSubmitting: (isSubmitting: boolean) => void
  ) => {
    // Validar que se haya seleccionado al menos una zona
    if (userZones.length === 0) {
      setZonesErrorMessage('Debe seleccionar al menos una Zona.')
      setSubmitting(false) // Reactiva el botón
      return
    }

    values.zones =
      zones !== undefined
        ? zones.filter((z) => userZones.includes(z.zoneId))
        : values.zones

    if (values.userId) {
      apiCall('PUT', `${ApiConfig.UserApi}/${values.userId}`, values)
        .then(() =>
          handleSnackbar({
            open: true,
            message: 'Usuario actualizado correctamente',
            duration: 3000,
            status: 'success'
          })
        )
        .catch(({ data }) => {
          const { message } = data
          handleSnackbar({
            open: true,
            message:
              message || 'Hubo un error al intentar actualizar el usuario.',
            duration: 3000,
            status: 'error'
          })
        })
        .finally(() => closeModal())
    } else {
      const createUser = (userToCreate: User): UserCreate => ({
        userName: userToCreate.userName.trim(),
        userAlias: userToCreate.userAlias,
        active: userToCreate.active,
        zones: userToCreate.zones.map((z) => z.zoneName)
      })
      apiCall('POST', `${ApiConfig.UserApi}`, createUser(values))
        .then(() =>
          handleSnackbar({
            open: true,
            message: 'Usuario creado correctamente',
            duration: 3000,
            status: 'success'
          })
        )
        .catch(({ data }) => {
          const { message } = data
          handleSnackbar({
            open: true,
            message: message || 'Hubo un error al intentar crear el usuario.',
            duration: 3000,
            status: 'error'
          })
        })
        .finally(() => closeModal())
    }
  }

  // Setea las zonas asociadas al usuario
  React.useEffect(() => {
    setUserZones(user?.zones?.map((z) => z.zoneId) || [])
  }, [])

  // Setea el almacen id en caso de tener zonas asociadas
  React.useEffect(() => {
    if (user && user.zones?.length > 0) {
      setWarehouseId(user.zones[0].warehouse.id!)
    }
  }, [])

  // Setea las zonas por almacén al inicio del modulo
  React.useEffect(() => {
    handleSelectedWarehouseZones(zones!)
  }, [zones, warehouseId])

  return (
    <Fragment>
      <Formik
        initialValues={user && user.userId ? user : initialValues}
        onSubmit={async (values: User, { setSubmitting }) => {
          await handleSubmit(values, setSubmitting)
        }}
        validationSchema={userValidationSchema}
      >
        {({ isSubmitting, setFieldValue }) => (
          <Form>
            <Grid container spacing={5} style={{ minWidth: '400px' }}>
              <Grid item xs={6}>
                <Field
                  fullWidth
                  name='userName'
                  id='userName'
                  component={TextField}
                  label='Email'
                  disabled={!!(user && user.userId)}
                  variant='outlined'
                />
              </Grid>
              <Grid item xs={5}>
                <Field
                  fullWidth
                  name='userAlias'
                  id='userAlias'
                  component={TextField}
                  label='Nombre'
                  disabled={!!(user && user.userId)}
                  variant='outlined'
                />
              </Grid>
              {user && user.userId ? (
                <Grid item xs={6}>
                  <Field
                    fullWidth
                    name='position.positionName'
                    id='position.positionName'
                    component={TextField}
                    label='Posición'
                    disabled
                    variant='outlined'
                  />
                </Grid>
              ) : (
                <Grid
                  item
                  xs={1}
                  sm={1}
                  style={{ padding: '0px', paddingTop: '20px' }}
                >
                  <Tooltip title='La posición de tránsito se creará con base en el email del usuario'>
                    <IconButton
                      style={{ padding: '0px', paddingTop: '14px' }}
                      className={button}
                    >
                      <Info />
                    </IconButton>
                  </Tooltip>
                </Grid>
              )}
              <Grid item xs={6} sm={2}>
                <Field
                  name='active'
                  component={CheckboxWithLabel}
                  Label={{ label: 'Activo' }}
                  type='checkbox'
                  color='default'
                />
              </Grid>
            </Grid>

            <Grid container spacing={5}>
              <Grid item xs={11} sm={11} lg={11}>
                <InputLabel id='selectedWarehouse'>Almacén</InputLabel>
                <Field
                  fullWidth
                  component={Select}
                  variant='outlined'
                  id='warehouseId'
                  name='warehouseId'
                  labelId='selectedWarehouse'
                  value={warehouseId}
                  onChange={(e: any) => {
                    setFieldValue('warehouseId', e.target.value as number)
                    handleSelectedWarehouseZones(
                      zones!,
                      e.target.value as number
                    )
                  }}
                >
                  <MenuItem key={-1} value={-1}>
                    {'Seleccionar Almacén...'}
                  </MenuItem>
                  {warehouses !== undefined
                    ? warehouses.map(({ id, warehouseName }, index) => (
                        <MenuItem key={`warehouseId${index}`} value={id}>
                          {warehouseName}
                        </MenuItem>
                      ))
                    : []}
                </Field>
                <p
                  className='Mui-error MuiFormHelperText-contained MuiFormHelperText-root'
                  style={{
                    fontSize: '12px',
                    marginLeft: '14px',
                    marginTop: '8px',
                    color: 'red'
                  }}
                >
                  <ErrorMessage name='warehouseId' />
                </p>
              </Grid>
              <Grid
                item
                xs={12}
                sm={1}
                style={{ padding: '0px', paddingTop: '20px' }}
              >
                <Tooltip title='Solo se podrán asignar zonas de un mismo almacén'>
                  <IconButton
                    style={{ padding: '0px', paddingTop: '14px' }}
                    className={button}
                  >
                    <Info />
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>
            <Grid container spacing={5}>
              <Grid item xs={12}>
                <div className={chipBoxZones}>
                  {zonesByWarehouse.length > 0
                    ? zonesByWarehouse
                        .filter((zone) => zone.active)
                        .map(({ zoneId, zoneName }) => (
                          <Chip
                            key={zoneId}
                            onClick={() => handleClickedChip(zoneId)}
                            color={
                              userZones.includes(zoneId) ? 'primary' : 'default'
                            }
                            label={zoneName}
                          />
                        ))
                    : ''}
                </div>
                {zonesErrorMessage && (
                  <p
                    className='Mui-error MuiFormHelperText-contained MuiFormHelperText-root'
                    style={{
                      fontSize: '12px',
                      marginLeft: '14px',
                      marginTop: '8px',
                      color: 'red'
                    }}
                  >
                    {zonesErrorMessage}
                  </p>
                )}
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant='contained'
                  color='primary'
                  fullWidth
                  type='submit'
                  disabled={isSubmitting}
                >
                  {user && user.userId ? 'Actualizar' : 'Crear'}
                </Button>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </Fragment>
  )
}
